import type { Api } from './api';
import { isAfterDark } from './format';
import type { ShareLink } from './types';

/** Links made for a ride, so the app can say "your link is live" and stop it. */
const KEY = (rideId: string) => `zr-share-${rideId}`;
export function savedLink(rideId: string): (ShareLink & { auto?: boolean }) | null {
  try {
    return JSON.parse(localStorage.getItem(KEY(rideId)) ?? 'null');
  } catch {
    return null;
  }
}
export function saveLink(rideId: string, link: (ShareLink & { auto?: boolean }) | null) {
  try {
    if (link) localStorage.setItem(KEY(rideId), JSON.stringify(link));
    else localStorage.removeItem(KEY(rideId));
  } catch {
    /* ignore */
  }
}

/** The page anyone can open to follow the trip (served by this app at /t/<token>). */
export const trackUrl = (token: string) => `${window.location.origin}/t/${token}`;

export async function ensureLink(api: Api, rideId: string, auto = false) {
  const have = savedLink(rideId);
  if (have) return have;
  const link = { ...(await api.share(rideId)), auto };
  saveLink(rideId, link);
  return link;
}

/** Phone share sheet when there is one; otherwise copy the link. Returns what happened, for a toast. */
export async function shareText(title: string, text: string, url: string): Promise<'shared' | 'copied' | 'failed'> {
  const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
  if (nav.share) {
    try {
      await nav.share({ title, text, url });
      return 'shared';
    } catch {
      /* closed, fall through to copy */
    }
  }
  try {
    await navigator.clipboard.writeText(`${text} ${url}`);
    return 'copied';
  } catch {
    return 'failed';
  }
}

/**
 * After-dark sharing (Account → "Share trips after dark", `shareTripsAfterDark` on the rider): once a driver is
 * coming, between 7 pm and 6 am India time, with at least one trusted contact, the app makes the live link by
 * itself (once per ride). Nothing is texted — no SMS provider — the rider sends it with Share.
 */
export function shouldAutoShare(o: { sharingOn: boolean; contacts: number; screen: string | null; hasLink: boolean; now?: Date }): boolean {
  return o.sharingOn && o.contacts > 0 && !o.hasLink && (o.screen === 'arriving' || o.screen === 'ontrip') && isAfterDark(o.now);
}
